import type { Project, ProjectEvent, ProjectEventKind } from '../types';

const KIND_ORDER: ProjectEventKind[] = [
  'task-added', 'task-renamed', 'task-status', 'task-deleted', 'task-moved-phase', 'task-due-changed', 'task-milestone',
  'phase-added', 'phase-renamed', 'phase-deleted',
  'milestone-added', 'milestone-renamed', 'milestone-completed', 'milestone-reopened', 'milestone-deleted',
  'project-edited', 'project-completed', 'project-reopened', 'project-archived', 'project-restored',
];
const PROJECT_FIELDS = [
  ['title', 'title'], ['outcome', 'outcome'], ['description', 'description'],
  ['startDate', 'start date'], ['dueDate', 'due date'], ['priority', 'priority'], ['complexity', 'complexity'],
] as const;

/** Snapshot-only change detection. The ID factory is injectable for deterministic tests. */
export function diffProjectActivity(
  before: Project | undefined, after: Project | undefined, at: string,
  makeId: () => string = () => crypto.randomUUID(),
): ProjectEvent[] {
  if (!before || !after) return [];
  const events: Omit<ProjectEvent, 'id'>[] = [];
  const add = (kind: ProjectEventKind, detail: Omit<ProjectEvent, 'id' | 'at' | 'kind'>) => {
    events.push({ at, kind, ...detail });
  };
  const oldTasks = new Map(before.tasks.map(task => [task.id, task]));
  const newTasks = new Map(after.tasks.map(task => [task.id, task]));
  for (const task of after.tasks) {
    const old = oldTasks.get(task.id);
    const detail = { itemId: task.id, title: task.title };
    if (!old) { add('task-added', detail); continue; }
    if (old.title !== task.title) add('task-renamed', { ...detail, title: old.title, from: old.title, to: task.title });
    if (old.status !== task.status) add('task-status', { ...detail, from: old.status, to: task.status });
    if (old.phaseId !== task.phaseId) add('task-moved-phase', { ...detail,
      from: before.phases.find(phase => phase.id === old.phaseId)?.title ?? 'No phase',
      to: after.phases.find(phase => phase.id === task.phaseId)?.title ?? 'No phase',
    });
    if ((old.dueDate ?? '') !== (task.dueDate ?? '')) add('task-due-changed', { ...detail, from: old.dueDate ?? '', to: task.dueDate ?? '' });
    // A milestone is a task flag (2026-09-16), so turning it on or off is a recorded change.
    if (!!old.isMilestone !== !!task.isMilestone) add('task-milestone', { ...detail, from: old.isMilestone ? 'milestone' : 'task', to: task.isMilestone ? 'milestone' : 'task' });
  }
  for (const task of before.tasks) if (!newTasks.has(task.id)) add('task-deleted', { itemId: task.id, title: task.title });
  const oldPhases = new Map(before.phases.map(phase => [phase.id, phase]));
  const newPhases = new Map(after.phases.map(phase => [phase.id, phase]));
  for (const phase of after.phases) {
    const old = oldPhases.get(phase.id);
    if (!old) add('phase-added', { itemId: phase.id, title: phase.title });
    else if (old.title !== phase.title) add('phase-renamed', { itemId: phase.id, title: old.title, from: old.title, to: phase.title });
  }
  for (const phase of before.phases) if (!newPhases.has(phase.id)) add('phase-deleted', { itemId: phase.id, title: phase.title });
  // No milestone block: `Project.milestones` is emptied by convertLegacyMilestones, and a
  // milestone is a flagged task whose changes are recorded by the task rules above.
  const changed = PROJECT_FIELDS.filter(([key]) => (before[key] ?? '') !== (after[key] ?? '')).map(([, label]) => label);
  if (changed.length) add('project-edited', { title: after.title, to: changed.join(', ') });
  if (before.status !== after.status) {
    if (after.status === 'completed') add('project-completed', { title: after.title });
    if (before.status === 'completed' && after.status === 'active') add('project-reopened', { title: after.title });
    if (after.status === 'archived') add('project-archived', {});
    if (before.status === 'archived') add('project-restored', {});
  }
  return events.sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind))
    .map(event => ({ ...event, id: makeId() }));
}

export function appendActivity(project: Project, events: ProjectEvent[]): ProjectEvent[] {
  return [...events, ...(project.activity ?? [])].slice(0, 200);
}
